import { queueMacrotask } from 'module/helpers/macrotask';
import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { SuggestionProps } from '@tiptap/suggestion';
import { Root } from 'react-dom/client';
import { portalWithAppContext } from 'components/organisms/app-context/portal-with-context';
import {
  CommandPickerExtensionOptions,
  CommandPickerExtensionStorage,
  CommandPickerLeafItem,
  CommandPickerItem,
} from './types';
import { CommandPickerPopoverRef, CommandsPickerPopover } from './ui/picker';

const commandsPickerName = 'commandsPicker';

export const CommandsPicker = Extension.create<
  CommandPickerExtensionOptions,
  CommandPickerExtensionStorage
>({
  name: commandsPickerName,
  addOptions() {
    return {
      items: [],
    };
  },
  addStorage: () => ({
    rect: new DOMRect(),
  }),
  addProseMirrorPlugins() {
    return [
      Suggestion<CommandPickerItem, CommandPickerLeafItem>({
        pluginKey: new PluginKey(commandsPickerName),
        editor: this.editor,
        char: '/',
        allowSpaces: true,
        allow: () => true,
        command: ({ editor, props: selected, range }) => {
          const { view, state } = editor;
          const { $head, $from } = view.state.selection;
          const currentPos = $from.pos;

          const startPos = $head?.nodeBefore
            ? currentPos -
              ($head.nodeBefore.text?.substring(
                $head.nodeBefore.text?.indexOf('/'),
              ).length ?? 0)
            : $from.start();

          const tr = state.tr.deleteRange(startPos, currentPos);

          view.dispatch(tr);
          queueMacrotask(() => {
            selected.execute(editor, range);
          });
          view.focus();
        },
        render: () => {
          let root: Root | null = null;
          let portalContainer: HTMLDivElement | null = null;
          let ref: CommandPickerPopoverRef | null = null;
          const getRect = (props: SuggestionProps) => {
            return (
              props.clientRect?.() ??
              props.editor.storage[commandsPickerName].rect
            );
          };

          return {
            onStart: (props) => {
              portalContainer = document.createElement('div');

              document.body.appendChild(portalContainer);

              root = portalWithAppContext({
                container: portalContainer,
                children: (
                  <CommandsPickerPopover
                    editor={this.editor}
                    popoverState={{ visible: true, position: getRect(props) }}
                    suggestionProps={props}
                    ref={(el) => {
                      ref = el;
                    }}
                    items={this.options.items}
                    hidePopover={() => {}}
                  />
                ),
              });
            },
            onUpdate: (props) => {
              ref?.update(props);
            },
            onKeyDown: (props) => {
              return ref?.onKeyDown?.(props) ?? false;
            },
            onExit: () => {
              ref = null;
              portalContainer?.remove();
              root?.unmount();
            },
          };
        },
      }),
    ];
  },
});
